import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../lib/auth-context';
import { userAPI, seedAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { RadioTower, Loader2, Eye, EyeOff, ArrowRight, User, Mail, Phone, Lock, KeyRound, ShieldCheck } from 'lucide-react';

const registerSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().min(10, 'Phone number must be at least 10 digits'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  confirmPassword: z.string(),
  role: z.enum(['admin', 'distributor', 'retailer']),
  parent_id: z.string().optional(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

const Register = () => {
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [distributors, setDistributors] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: { role: '', parent_id: '' },
  });
  const role = watch('role');

  useEffect(() => { seedAPI.seed().catch(() => {}); }, []);
  useEffect(() => { if (role === 'retailer') fetchDistributors(); }, [role]);

  const fetchDistributors = async () => {
    try { const r = await userAPI.getUsers('distributor'); setDistributors(r.data); } catch {}
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const { confirmPassword, ...registerData } = data;
      if (data.role !== 'retailer') delete registerData.parent_id;
      const user = await registerUser(registerData);
      toast.success(`Welcome, ${user.name}! Account created.`);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally { setLoading(false); }
  };

  const handleRoleChange = (value) => {
    setValue('role', value); setSelectedRole(value);
    if (value !== 'retailer') setValue('parent_id', '');
  };

  const InputField = ({ id, label, icon: Icon, error, children, ...props }) => (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</Label>
      <div className="relative">
        <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        {children || <Input {...register(id)} className="pl-11 h-11 bg-slate-50/80 border-slate-200 rounded-xl text-sm" {...props} data-testid={`register-${id}-input`} />}
      </div>
      {error && <p className="text-xs text-red-500">{error.message}</p>}
    </div>
  );

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-8 bg-white relative overflow-auto">
        <div className="absolute inset-0 dot-grid-bg pointer-events-none" />
        <div className="w-full max-w-[420px] relative z-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#4680ff] to-[#2563eb] shadow-lg shadow-blue-500/25">
              <RadioTower className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <div className="leading-none">
              <span className="text-[16px] font-bold text-slate-900 tracking-wide block">TRIPATHY</span>
              <span className="text-[10px] font-bold text-orange-500 tracking-[0.2em]">TELECOM</span>
            </div>
          </div>

          <div className="mb-6">
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Create account</h1>
            <p className="text-slate-500 mt-1 text-sm">Join our network and start earning</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <InputField id="name" label="Full Name" icon={User} error={errors.name} placeholder="Enter your name" />
            <InputField id="email" label="Email" icon={Mail} error={errors.email} placeholder="you@example.com" type="email" />
            <InputField id="phone" label="Phone Number" icon={Phone} error={errors.phone} placeholder="10-digit number" />

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Account Type</Label>
              <Select onValueChange={handleRoleChange} value={selectedRole}>
                <SelectTrigger className="h-11 rounded-xl bg-slate-50/80 border-slate-200" data-testid="register-role-select"><SelectValue placeholder="Select account type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="distributor">Distributor</SelectItem>
                  <SelectItem value="retailer">Retailer</SelectItem>
                </SelectContent>
              </Select>
              {errors.role && <p className="text-xs text-red-500">{errors.role.message}</p>}
            </div>

            {selectedRole === 'retailer' && (
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Distributor</Label>
                {distributors.length > 0 ? (
                  <Select onValueChange={(value) => setValue('parent_id', value)}>
                    <SelectTrigger className="h-11 rounded-xl bg-slate-50/80 border-slate-200" data-testid="register-distributor-select"><SelectValue placeholder="Select your distributor" /></SelectTrigger>
                    <SelectContent>{distributors.map(d => <SelectItem key={d.id} value={d.id}>{d.name} ({d.email})</SelectItem>)}</SelectContent>
                  </Select>
                ) : <p className="text-xs text-slate-400">No distributors available. Contact admin.</p>}
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input type={showPassword ? 'text' : 'password'} placeholder="Min 6 characters" {...register('password')} className="pl-11 pr-11 h-11 bg-slate-50/80 border-slate-200 rounded-xl text-sm" data-testid="register-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-500">{errors.password.message}</p>}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Confirm Password</Label>
              <div className="relative">
                <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input type="password" placeholder="Re-enter password" {...register('confirmPassword')} className="pl-11 h-11 bg-slate-50/80 border-slate-200 rounded-xl text-sm" data-testid="register-confirm-password-input" />
              </div>
              {errors.confirmPassword && <p className="text-xs text-red-500">{errors.confirmPassword.message}</p>}
            </div>

            <Button type="submit" disabled={loading} className="w-full h-12 text-sm font-semibold rounded-lg text-white bg-[#4680ff] hover:bg-[#2563eb] shadow-lg shadow-blue-500/20" data-testid="register-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><span>Create Account</span><ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </form>

          <p className="text-center mt-6 text-sm text-slate-500">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-[#4680ff] hover:text-[#2563eb] transition-colors" data-testid="login-link">Sign in</Link>
          </p>
        </div>

        <div className="absolute bottom-6 left-0 right-0 text-center">
          <p className="text-xs text-slate-400 font-medium tracking-wide opacity-70">Developer by <span className="font-semibold text-slate-500">Supro</span></p>
        </div>
      </div>

      {/* Right - Branding */}
      <div className="hidden lg:flex lg:w-[42%] relative overflow-hidden items-center justify-center" style={{ background: 'linear-gradient(160deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)' }}>
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-72 h-72 rounded-full bg-[#4680ff]/10 blur-3xl" />
          <div className="absolute bottom-32 left-16 w-56 h-56 rounded-full bg-orange-500/8 blur-3xl" />
        </div>
        <div className="relative z-10 px-12 max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-white/[0.08] backdrop-blur-sm flex items-center justify-center mb-8 ring-1 ring-white/[0.1]">
            <ShieldCheck className="w-8 h-8 text-white" strokeWidth={1.5} />
          </div>
          <h2 className="text-3xl font-bold text-white">Join Our<br /><span className="text-orange-400">Network</span></h2>
          <p className="mt-4 text-white/40 text-sm leading-relaxed">Start your mobile recharge business today. Become a distributor or retailer and earn commissions on every transaction.</p>
          <div className="mt-10 space-y-5">
            {[
              { num: '1', title: 'Register as Admin', desc: 'Create your admin account first' },
              { num: '2', title: 'Add Distributors', desc: 'Admin creates distributor accounts' },
              { num: '3', title: 'Add Retailers', desc: 'Distributors manage their retailers' },
            ].map(step => (
              <div key={step.num} className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-orange-500/20 text-orange-400 flex items-center justify-center text-xs font-bold flex-shrink-0">{step.num}</div>
                <div>
                  <h3 className="text-white text-sm font-semibold">{step.title}</h3>
                  <p className="text-white/30 text-xs">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
